import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { convertConstStringToText, useLocalization } from '@ui-framework/index';
import CustomIcons from './IconLibrary';

export const dispatchEventStatusConfig = (theme, formatMessage) => ({
  PENDING_ACCEPTANCE: {
    id: 'PENDING_ACCEPTANCE',
    label: formatMessage({
      id: 'PENDING_ACCEPTANCE',
      defaultMessage: 'Pending Acceptance',
    }),
    borderColor: theme.palette.primary.dark,
    textColor: 'warning',
    Icon: CustomIcons.DISPATCH_EVENT_STATUS_PENDING_ACCEPTANCE,
    iconColor: 'warning',
  },
  ACCEPTED: {
    id: 'ACCEPTED',
    label: formatMessage({
      id: 'ACCEPTED',
      defaultMessage: 'Accepted',
    }),
    borderColor: theme.palette.primary.dark,
    textColor: 'success',
    Icon: CustomIcons.DISPATCH_EVENT_STATUS_ACCEPTED,
    iconColor: 'success',
  },
  REJECTED: {
    id: 'REJECTED',
    label: formatMessage({
      id: 'REJECTED',
      defaultMessage: 'Rejected',
    }),
    borderColor: theme.palette.primary.dark,
    textColor: 'error',
    Icon: CustomIcons.DISPATCH_EVENT_STATUS_REJECTED,
    iconColor: 'error',
  },
  ACCEPTED_WITH_CONDITIONS: {
    id: 'ACCEPTED_WITH_CONDITIONS',
    label: formatMessage({
      id: 'ACCEPTED_WITH_CONDITIONS',
      defaultMessage: 'Accepted With Conditions',
    }),
    borderColor: theme.palette.primary.dark,
    textColor: 'success',
    Icon: CustomIcons.DISPATCH_EVENT_STATUS_ACCEPTED_WITH_CONDITIONS,
    iconColor: 'success',
  },
  REVOKED: {
    id: 'REVOKED',
    label: formatMessage({
      id: 'REVOKED',
      defaultMessage: 'Revoked',
    }),
    borderColor: theme.palette.primary.dark,
    textColor: 'error',
    Icon: CustomIcons.DISPATCH_EVENT_STATUS_REVOKED,
    iconColor: 'error',
  },
  ASSIGNED: {
    id: 'ASSIGNED',
    label: formatMessage({
      id: 'ASSIGNED',
      defaultMessage: 'Assigned',
    }),
    borderColor: theme.palette.primary.dark,
    textColor: 'success',
    Icon: CustomIcons.DISPATCH_EVENT_STATUS_ASSIGNED,
    iconColor: 'success',
  },
  SCHEDULED: {
    id: 'SCHEDULED',
    label: formatMessage({
      id: 'SCHEDULED',
      defaultMessage: 'Scheduled',
    }),
    borderColor: theme.palette.primary.dark,
    textColor: 'success',
    Icon: CustomIcons.DISPATCH_EVENT_STATUS_SCHEDULED,
    iconColor: 'success',
  },
  ALL: {
    id: 'ALL',
    label: formatMessage({
      id: 'ALL',
      defaultMessage: 'All',
    }),
    borderColor: theme.palette.primary.dark,
    textColor: 'primary',
  },
  INITIAL_DISPATCHED_LOCATION: {
    label: formatMessage({
      id: 'INITIAL_DISPATCHED_LOCATION',
      defaultMessage: 'Initial Dispatched Location',
    }),
    borderColor: theme.palette.warning.dark,
    textColor: 'warning',
    Icon: CustomIcons.DISPATCH_EVENT_STATUS_SCHEDULED,
    iconColor: 'error',
  },
});

function DispatchEventStatus({
  value,
  count = 0,
  showCount = false,
  withIcon = false,
  onClick = () => undefined,
  selected = false,
  showAsText = false,
  small = false,
  showAsTextProps = {},
}) {
  const theme = useTheme();
  const { formatMessage } = useLocalization();
  const config = dispatchEventStatusConfig(theme, formatMessage);

  const smallText = small ? { fontSize: '14px' } : {};

  const getToolTipText = ({ selectedEventStatus, label, id }) => {
    const statusText = id === 'ALL' ? 'Statuses' : 'Status';
    const eventText = selectedEventStatus
      ? formatMessage({ id: 'SHOWING', defaultMessage: 'Showing' })
      : formatMessage({ id: 'CLICK_TO_SHOW', defaultMessage: 'Click To Show' });
    return `${eventText} ${formatMessage({ id: label, defaultMessage: label })} ${statusText}`;
  };

  if (config?.[value]) {
    const { Icon, label, textColor, iconColor, id } = config[value];
    return showAsText ? (
      <Typography
        sx={{
          color: theme.palette[textColor]?.main,
          ...smallText,
          ...(showAsTextProps?.sx || {}),
        }}>
        {label}
      </Typography>
    ) : (
      <Tooltip
        arrow
        placement="top"
        title={getToolTipText({ selectedEventStatus: selected, label, id })}>
        <Button
          sx={{ height: '30px' }}
          variant={selected ? 'contained' : 'outlined'}
          color={textColor}
          onClick={onClick}>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Stack direction="row" spacing={0.4}>
              {withIcon && Icon && (
                <Icon color={iconColor} fontSize="small" sx={{ mr: 1 }} />
              )}
              <span>{label}</span>
            </Stack>
            {showCount && (
              <Chip
                sx={{
                  ml: 0.5,
                  color: selected ? '#ffffff' : '#000000',
                  fontWeight: 'bold',
                  border: 0,
                  p: 0,
                }}
                label={count}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Stack>
        </Button>
      </Tooltip>
    );
  }

  return showAsText ? (
    <Typography sx={{ ...smallText }} color="inherit">
      {convertConstStringToText(value)}
    </Typography>
  ) : (
    <Button
      variant={selected ? 'contained' : 'outlined'}
      color="inherit"
      size="small"
      sx={{ minWidth: 0 }}>
      <Box display="flex" alignItems="center">
        <span>{convertConstStringToText(value)}</span>
      </Box>
      {showCount && (
        <Chip
          sx={{
            ml: 1,
            backgroundColor: 'background.paper',
            color: 'text.primary',
            fontWeight: 'bold',
          }}
          label={count}
          size="small"
          color="secondary"
          variant="outlined"
        />
      )}
    </Button>
  );
}

export default DispatchEventStatus;
